import React from 'react';
import { Box, Typography, Button, Tooltip, SvgIconProps } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactElement<SvgIconProps>;
  onRefresh?: () => void;
  refreshDisabled?: boolean;
  actions?: React.ReactNode;
  compact?: boolean;
}

/**
 * A reusable compact page header component
 */
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  icon,
  onRefresh,
  refreshDisabled = false,
  actions,
  compact = true,
}) => {
  return (
    <Box 
      className="page-header"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: compact ? 1 : 2,
        py: compact ? 1 : 2,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box 
        className="header-left"
        sx={{ 
          display: 'flex',
          flexDirection: compact ? 'row' : 'column',
          alignItems: compact ? 'center' : 'flex-start',
          gap: compact ? 2 : 0.5,
        }}
      >
        <Box 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {icon && React.cloneElement(icon, { 
            sx: { 
              fontSize: compact ? 24 : 32,
              color: 'primary.main',
            }
          })}
          <Typography 
            variant={compact ? "h6" : "h5"} 
            component="h1" 
            sx={{ 
              fontWeight: 'bold',
              m: 0,
            }}
          >
            {title}
          </Typography>
        </Box>
        
        {subtitle && (
          <Typography 
            variant={compact ? "body2" : "body1"} 
            color="text.secondary"
            sx={{
              maxWidth: '600px',
              display: compact ? { xs: 'none', sm: 'block' } : 'block',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      
      <Box 
        className="header-actions"
        sx={{ 
          display: 'flex',
          gap: 1,
          alignItems: 'center',
        }}
      >
        {onRefresh && (
          <Tooltip title="Refresh data">
            <span>
              <Button
                variant="outlined"
                size={compact ? "small" : "medium"}
                startIcon={<RefreshIcon />}
                onClick={onRefresh}
                disabled={refreshDisabled}
                sx={{ minWidth: compact ? '32px' : 'auto' }}
              >
                {!compact && "Refresh"}
              </Button>
            </span>
          </Tooltip>
        )}
        
        {actions}
      </Box>
    </Box>
  );
};

export default PageHeader;
